/* eslint-disable multiline-ternary */

import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	reduxForm,
	FormSection,
	Field,
	change,
	clearFields,
	initialize,
	getFormValues,
	getFormSyncErrors,
	submit
} from "redux-form";
import {
	Button,
	ReduxControls,
	Icon,
	ErrorBanner,
	ControlWrapper
} from "rhombus-react";

import { transcodeVideo } from "reducers/Assets";
import { buildSelectedAssetsToTranscode } from "selectors/Assets";
import { noop, safeDecodeURIComponent, splitFilename } from "utils/common";
import {
	inRange,
	isInteger,
	requiredNotNull,
	validVideoNameCharacters,
	validVideoNameCharactersLegacyInnovid,
	videoNameMaxLength
} from "widgets/forms/validators/validators";
import { useFlag } from "@innovid/feature-flags-react";
import { FEATURE_FLAGS } from "featureFlag/featureFlag";

import BasicModal from "widgets/modals/BasicModal";
import ProgressContainer from "widgets/ProgressContainer";

import "./BulkTranscodeModal.css";

const BULK_TRANSCODE = "bulk-transcode";

const ADVANCED_SETTINGS_NAMES = [
	"video_bitrate",
	"audio_bitrate",
	"audio_sample_rate",
	"audio_gain",
	"frame_rate",
];

const SETTINGS_NAMES = [
	...ADVANCED_SETTINGS_NAMES,
	"width",
	"height",
];

const SETTINGS_LABELS = {
	video_bitrate: "Video Bitrate",
	audio_bitrate: "Audio Bitrate",
	audio_sample_rate: "Audio Sample Rate",
	audio_gain: "Audio Gain",
	frame_rate: "Frame Rate",
	width: "Width",
	height: "Height",
	name: "Name",
};

const LABEL_INDICES = Object.values(SETTINGS_LABELS).reduce((prev, value, index) => {
	prev[value] = index;
	return prev;
}, {});

const SETTINGS_PROPS = {
	component: ReduxControls.Input,
	type: "number",
	placeholder: "Auto",
	step: 1,
};

const BOUNDS = {
	WIDTH: { // pixels
		min: 16,
		max: 9999,
	},
	HEIGHT: { // pixels
		min: 16,
		max: 9999,
	},
	VIDEO_BITRATE: { // kbps
		min: 100,
		max: 30000,
	},
	AUDIO_BITRATE: { // kbps
		min: 16,
		max: 320,
	},
	AUDIO_SAMPLERATE: { // hz
		min: 8000,
		max: 96000,
	},
	AUDIO_GAIN: { // Db
		min: -60,
		max: 60,
	},
	FRAMERATE: {
		min: 20,
		max: 60,
	},
};

const widthProps = {
	name: "width",
	...SETTINGS_PROPS,
	...BOUNDS.WIDTH,
	placeholder: "",
	validate: [requiredNotNull, isInteger, inRange(BOUNDS.WIDTH)],
};

const heightProps = {
	name: "height",
	...SETTINGS_PROPS,
	...BOUNDS.HEIGHT,
	placeholder: "",
	validate: [requiredNotNull, isInteger, inRange(BOUNDS.HEIGHT)],
};

const videoBitrateProps = {
	name: "video_bitrate",
	...SETTINGS_PROPS,
	...BOUNDS.VIDEO_BITRATE,
	validate: [isInteger, inRange(BOUNDS.VIDEO_BITRATE)],
};

const audioBitrateProps = {
	name: "audio_bitrate",
	...SETTINGS_PROPS,
	...BOUNDS.AUDIO_BITRATE,
	validate: [isInteger, inRange(BOUNDS.AUDIO_BITRATE)],
};

const audioSampleRateProps = {
	name: "audio_sample_rate",
	...SETTINGS_PROPS,
	...BOUNDS.AUDIO_SAMPLERATE,
	validate: [isInteger, inRange(BOUNDS.AUDIO_SAMPLERATE)],
};

const audioGainProps = {
	name: "audio_gain",
	...SETTINGS_PROPS,
	...BOUNDS.AUDIO_GAIN,
	validate: [isInteger, inRange(BOUNDS.AUDIO_GAIN)],
};

const frameRateProps = {
	name: "frame_rate",
	...SETTINGS_PROPS,
	...BOUNDS.FRAMERATE,
	validate: [isInteger, inRange(BOUNDS.FRAMERATE)],
};

const nameValidators = [requiredNotNull, validVideoNameCharacters, videoNameMaxLength];
const nameValidatorsLegacyInnovid = [requiredNotNull, validVideoNameCharactersLegacyInnovid, videoNameMaxLength];

export function sanitizeName(filename, supportLegacyInnovidSpecialChars = false){
	const [base] = splitFilename(safeDecodeURIComponent(filename));
	if(supportLegacyInnovidSpecialChars){
		return base.replace(/[^a-zA-Z0-9_\-+|:[\]'"?×}`<{>,&!$]+/g, "-");
	}
	return base.replace(/[^a-zA-Z0-9_\-+|:]+/g, "-");
}

export function generateInitialValues(assets = [], supportLegacyInnovidSpecialChars = false) {
	const videos = assets.reduce(
		(prev, video) => {
			const sanitizedName = sanitizeName(video.name, supportLegacyInnovidSpecialChars);
			const [width, height] = video.dimensions.split(" x ").map(dim => parseInt(dim));
			prev.videos[`id${video.id}`] = {
				...video,
				name: sanitizedName,
				width,
				height,
			};
			return prev;
		},
		{
			videos: {},
			showAdvanced: false,
			all: {
				maintainAspectRatio: false,
			},
		}
	);

	return videos;
}

function DimensionInput(props){
	/*
		Rhombus ReduxControls.Input's have a problem with their onChange not being
		passed the change event as the first argument (it's the newValue instead),
		which makes it impossible to prevent changes to invalid values with redux-form
		by using `event.preventDefault()`.

		Preventing changes to invalid values is the easiest way to handle maintaining
		aspect ratios, so we create a thin wrapper around an HTML `input`, but use
		Rhombus's ControlWrapper to give it the behavior around labels, error messages, etc.
	*/
	return (
		<input
			className = "e-form__input"
			type = "number"
			step = {1}
			{...props.input}
		/>
	);
}

const DimensionControl = ControlWrapper(DimensionInput);

export function validDimension(value){
	if(value <= 0){
		return "Value must be positive";
	}
	return isInteger(value);
}

export function updateNames(dispatch, assets, values, supportLegacyInnovidSpecialChars = false){
	const { prefix="", suffix="" } = values || {};
	assets.forEach((video) => {
		const fieldName = `videos.id${video.id}.name`;
		const sanitizedName = sanitizeName(video.name, supportLegacyInnovidSpecialChars);
		const existingName = values.videos?.[`id${video.id}`]?.name;
		const isNameDeepModified = existingName?.includes(sanitizedName) === false;

		if(!isNameDeepModified){
			// We pass a fourth argument to touch the field as well, so errors show immediately
			dispatch(change(BULK_TRANSCODE, fieldName, `${prefix}${sanitizedName}${suffix}`, true));
		}
	});
}

export function updateAll(dispatch, assets, fieldName, newValue){
	assets.forEach((video) => {
		const fieldNamePrefix = `videos.id${video.id}`;
		const targetField = `${fieldNamePrefix}.${fieldName}`;
		// We pass a fourth argument to touch the field as well, so errors show immediately
		dispatch(change(BULK_TRANSCODE, targetField, newValue, true));
	});
}

export function consolidateErrors(errors){
	/*
		Video fields are highlighted red to show that they have errors, but they don't show inline
		error messages.

		The error banner needs errors grouped by the field name (width, height, etc.), so that there
		aren't a lot of duplicate errors shown when the user does something like type in a bad value
		in a Settings Applied To All Videos field.

		This takes the errors object that looks like
		{
			videos: {
				id123: {
					width: "Value must be between 16 and 9999",
					audio_gain: "Value must be between -60 and 60"
				},
				id234: {
					width: "Value must be between 16 and 9999",
					height: "Value must be between 16 and 9999",
				},
				...
			}
		}

		And returns an array of strings like:
		[
			"Audio Gain - Value must be between -60 and 60"
			"Width - Value must be between 16 and 9999",
			"Height - Value must be between 16 and 9999",
		]
	*/
	const { videos } = errors || {};
	if(!videos){
		return [];
	}

	const messagesSet = Object.values(videos).reduce((prev, errorsForVideo) => {
		/*
			`errorsForVideo` is an object of error messages keyed by the video properties like:
			```
				{
					width: "Value must be between 16 and 9999",
					audio_gain: "Value must be between -60 and 60"
				}
			```
		*/
		Object.entries(errorsForVideo).forEach(([key, value]) => {
			/*
				We iterate through each entry and add a formatted message to the reducer's returned Set,
				to remove duplicates.

				e.g. for the object above we add:
					"Width - Value must be between 16 and 9999",
					"Audio Gain - Value must be between -60 and 60"
			*/
			const errorMessage = `${SETTINGS_LABELS[key]} - ${value}`;
			prev.add(errorMessage);
		});
		return prev;
	}, new Set());

	return [...messagesSet].sort((messageA, messageB) => {
		/*
			`messagesSet` is sorted by Set insertion order, which isn't what we want.
			This sorts the messages by the order the fields appear, from left to right
		*/
		const [labelA] = messageA.split(" - ");
		const [labelB] = messageB.split(" - ");
		return LABEL_INDICES[labelA] - LABEL_INDICES[labelB];
	});
}

function BulkTranscodeModal(props) {
	const {
		valid,
		onClose,
	} = props;

	const dispatch = useDispatch();
	const supportLegacyInnovidSpecialChars = useFlag(FEATURE_FLAGS.SUPPORT_LEGACY_INNOVID_SPECIAL_CHARS);
	const transcodable = useSelector(buildSelectedAssetsToTranscode);
	const isTranscoding = useSelector(state => state.assets.transcode?.isTranscoding);
	const values = useSelector(getFormValues(BULK_TRANSCODE));
	const errors = useSelector(getFormSyncErrors(BULK_TRANSCODE));

	const errorMessages = useMemo(() => consolidateErrors(errors), [errors]);
	const errorBanner =  errorMessages.length === 0 ? null : (
		<ErrorBanner
			title="Sorry, please fix the errors below and try again."
			messages={errorMessages}
		/>
	 );

	const {
		prefix,
		suffix,
		showAdvanced,
		aspectRatio,
		all,
	} = values;

	const {
		maintainAspectRatio,
		width,
		height,
	} = all || {};

	const isAspectRatioDisabled = !width || !height || errors?.all?.width || errors?.all?.height;

	const className = `basic-modal bulk-transcode-modal ${
		showAdvanced ? "advanced" : ""
	}`;

	useEffect(() => {
		if (!transcodable?.length) {
			// The modal is meaningless if there are no transcodable selected assets, so just close it.
			// This can happen if the user refreshes or directly routes to this modal's route.
			onClose();
			return;
		}

		const initialValues = generateInitialValues(transcodable, supportLegacyInnovidSpecialChars);
		dispatch(initialize(BULK_TRANSCODE, initialValues));
	}, [transcodable, supportLegacyInnovidSpecialChars]);

	useEffect(() => {
		updateNames(dispatch, transcodable, values, supportLegacyInnovidSpecialChars);
	}, [prefix, suffix]);

	useEffect(() => {
		/*
			If we end up with an invalid aspect ratio, e.g. if you change a specific video's dimensions,
			so it clears the Apply to All value, we abandon maintaining the aspect ratio.
		*/
		const ratio = width / height;
		if(!ratio || ratio === Infinity){
			dispatch(change(BULK_TRANSCODE, "all.maintainAspectRatio", false));
		}
	}, [width, height]);

	function resetNames(){
		const videoFieldNames = transcodable.map(video => `videos.id${video.id}.name`);
		dispatch(clearFields(BULK_TRANSCODE, false, false, "prefix", "suffix", ...videoFieldNames));
	}

	function resetSettings(){
		/*
			Settings refers to everything that's not the video name.
			This resets all video fields besides video names, and all settings applied to all videos
			fields, besides prefix and suffix.
		*/
		const fieldNames = transcodable.flatMap((video) => {
			const fieldNamePrefix = `videos.id${video.id}`;
			return SETTINGS_NAMES.map(name => `${fieldNamePrefix}.${name}`);
		});
		fieldNames.push(
			"all.maintainAspectRatio",
			...(SETTINGS_NAMES.map(name => `all.${name}`))
		);

		dispatch(clearFields(BULK_TRANSCODE, false, false, ...fieldNames));
	}

	function onConfirm() {
		dispatch(submit(BULK_TRANSCODE));
	}

	function toggleShowAdvanced() {
		dispatch(change(BULK_TRANSCODE, "showAdvanced", !showAdvanced));
	}

	const advancedFields = !showAdvanced ? null : (
		<>
			{/*
				We want to share Field properties between the Applied to All Fields and the Video Fields,
				but we don't want validation on any of the Applied to All Fields, because they can be
				overridden on the Video level, and only the Video fields need to be valid to submit properly.

				We just pass in an empty array for the `validate` prop, rather than make a whole extra set
				of consts, or inlining validation in the Video fields.
			*/}
			<Field
				label="Video Bitrate"
				{...videoBitrateProps}
				validate={[]}
				onChange={(event, newValue) => { updateAll(dispatch, transcodable, "video_bitrate", newValue); }}
			/>
			<Field
				label="Audio Bitrate"
				{...audioBitrateProps}
				validate={[]}
				onChange={(event, newValue) => { updateAll(dispatch, transcodable, "audio_bitrate", newValue); }}
			/>
			<Field
				label="Audio Sample Rate"
				{...audioSampleRateProps}
				validate={[]}
				onChange={(event, newValue) => { updateAll(dispatch, transcodable, "audio_sample_rate", newValue); }}
			/>
			<Field
				label="Audio Gain"
				{...audioGainProps}
				validate={[]}
				onChange={(event, newValue) => { updateAll(dispatch, transcodable, "audio_gain", newValue); }}
			/>
			<Field
				label="Frame Rate"
				{...frameRateProps}
				validate={[]}
				onChange={(event, newValue) => { updateAll(dispatch, transcodable, "frame_rate", newValue); }}
			/>
		</>
	);

	const videoFields = useMemo(() => {
		return transcodable.map((video) => {
			const fieldNamePrefix = `videos.id${video.id}`;
			const advancedVideoFields = !showAdvanced ? null : (
				<>
					<td className="video-bitrate-col">
						<Field
							{...videoBitrateProps}
							onChange = {() => { dispatch(clearFields(BULK_TRANSCODE, false, false, "all.video_bitrate")); }}
						/>
					</td>
					<td className="audio-bitrate-col">
						<Field
							{...audioBitrateProps}
							onChange = {() => { dispatch(clearFields(BULK_TRANSCODE, false, false, "all.audio_bitrate")); }}
						/>
					</td>
					<td className="audio-samplerate-col">
						<Field
							{...audioSampleRateProps}
							onChange = {() => { dispatch(clearFields(BULK_TRANSCODE, false, false, "all.audio_sample_rate")); }}
						/>
					</td>
					<td className="audio-gain-col">
						<Field
							{...audioGainProps}
							onChange = {() => { dispatch(clearFields(BULK_TRANSCODE, false, false, "all.audio_gain")); }}
						/>
					</td>
					<td className="framerate-col">
						<Field
							{...frameRateProps}
							onChange = {() => { dispatch(clearFields(BULK_TRANSCODE, false, false, "all.frame_rate")); }}
						/>
					</td>
				</>
			);
			return (
				<FormSection
					key={video.id}
					name={fieldNamePrefix}
				>
					<tr>
						{advancedVideoFields}
						<td>
							<Field
								{...widthProps}
								onChange = {() => { dispatch(clearFields(BULK_TRANSCODE, false, false, "all.width", "all.maintainAspectRatio")); }}
							/>
						</td>
						<td>
							<Field
								{...heightProps}
								onChange = {() => { dispatch(clearFields(BULK_TRANSCODE, false, false, "all.height", "all.maintainAspectRatio")); }}
							/>
						</td>
						<td>
							<Field
								name="name"
								component={ReduxControls.Input}
								validate={supportLegacyInnovidSpecialChars ? nameValidatorsLegacyInnovid : nameValidators}
							/>
						</td>
					</tr>
				</FormSection>
			);
		});
	}, [transcodable, showAdvanced, supportLegacyInnovidSpecialChars]);

	function renderContent() {
		return (
			<ProgressContainer isLoading={isTranscoding}>
				<div className="main-content">
					<div>
						{errorBanner}
						<div className="section-header">
							<h2>Settings Applied to All Videos</h2>
							<a
								className="advanced-toggle"
								onClick={toggleShowAdvanced}
							>
								{`${showAdvanced ? "Hide" : "Show"} Advanced`}
							</a>
						</div>
						<div>
							<div className="name-modifiers">
								<Field
									name="prefix"
									className="prefix"
									label="File Name Prefix"
									component={ReduxControls.Input}
								/>
								<Field
									name="suffix"
									className="suffix"
									label="File Name Suffix"
									component={ReduxControls.Input}
								/>
								<Icon
									className="reset"
									name="fas fa-undo"
									onClick={resetNames}
									data-tip="Reset Names"
								/>
							</div>
							<FormSection
								name="all"
							>
								<div className="all-videos-fields">
									{advancedFields}
									<Field
										label="Width"
										{...widthProps}
										component={DimensionControl}
										validate={[validDimension]}
										onChange={(event, newValue) => {
											if(validDimension(newValue) !== undefined){
												// Prevent changes to invalid values, to simplify maintaining aspect ratio
												event.preventDefault();
											} else {
												updateAll(dispatch, transcodable, "width", newValue);

												if(maintainAspectRatio){
													const targetHeight = Math.round(newValue / aspectRatio);
													if(targetHeight){
														dispatch(change(BULK_TRANSCODE, "all.height", targetHeight));
														updateAll(dispatch, transcodable, "height", targetHeight);
													}
												} else {
													const targetRatio = newValue / height;
													if(targetRatio && (targetRatio !== aspectRatio) && (targetRatio !== Infinity)){
														dispatch(change(BULK_TRANSCODE, "aspectRatio", targetRatio));
													}
												}
											}
										}}
									/>
									<Field
										label="Height"
										{...heightProps}
										component={DimensionControl}
										validate={[validDimension]}
										onChange={(event, newValue) => {
											if(validDimension(newValue) !== undefined){
												// Prevent changes to invalid values, to simplify maintaining aspect ratio
												event.preventDefault();
											} else {
												updateAll(dispatch, transcodable, "height", newValue);

												if(maintainAspectRatio){
													const targetWidth = Math.round(newValue * aspectRatio);
													if(targetWidth){
														dispatch(change(BULK_TRANSCODE, "all.width", targetWidth));
														updateAll(dispatch, transcodable, "width", targetWidth);
													}
												} else {
													const targetRatio = width / newValue;
													if(targetRatio && (targetRatio !== aspectRatio) && (targetRatio !== Infinity)){
														dispatch(change(BULK_TRANSCODE, "aspectRatio", targetRatio));
													}
												}
											}
										}}
									/>
									<Icon
										className="reset"
										name="fas fa-undo"
										onClick={resetSettings}
										data-tip="Reset Settings"
									/>
									<Field
										className="maintain-aspect-ratio"
										name="maintainAspectRatio"
										label="Maintain Aspect Ratio"
										useValue
										component={ReduxControls.Checkbox}
										disabled={isAspectRatioDisabled}
									/>
								</div>
							</FormSection>
						</div>
					</div>

					<hr />
					<div>
						<h2>Videos</h2>
						<table>
							<thead>
								<tr>
									{!showAdvanced ? null : (
										<>
											<th className="video-bitrate-col">
												Video Bitrate
											</th>
											<th className="audio-bitrate-col">
												Audio Bitrate
											</th>
											<th className="audio-samplerate-col">
												Audio Sample Rate
											</th>
											<th className="audio-gain-col">
												Audio Gain
											</th>
											<th className="framerate-col">
												Frame Rate
											</th>
										</>
									)}
									<th className="width-col required">Width</th>
									<th className="height-col required">Height</th>
									<th className="name-col required">Name</th>
								</tr>
							</thead>
							<tbody>{videoFields}</tbody>
						</table>
					</div>
				</div>
			</ProgressContainer>
		);
	}

	function renderFooter() {
		return (
			<>
				<Button theme="default" onClick={onClose}>
					Cancel
				</Button>
				<Button
					onClick={onConfirm}
					disabled={!valid || isTranscoding}
				>
					Confirm & Transcode
				</Button>
			</>
		);
	}

	const modalProps = {
		title: "Transcode Videos",
		className,
		onClose,
		renderContent,
		renderFooter,
	};

	return <BasicModal {...modalProps} />;
}

export default reduxForm({
	form: BULK_TRANSCODE,
	initialValues: {
		showAdvanced: false,
		all: {
			maintainAspectRatio: false,
		},
	},
	onSubmit(values, dispatch, formProps) {
		const { libraryId, onClose } = formProps;
		const videos = Object.values(values.videos);
		const type = "job:encode_video";
		const items = videos.map((video) => {
			const advancedValues = ADVANCED_SETTINGS_NAMES.reduce((prev, name) => {
				const value = video[name];
				if(value !== undefined){
					prev[name] = value;
				}
				return prev;
			}, {});

			const hasAdvancedValues = Object.keys(advancedValues).length;

			const item = {
				advvast: false,
				name: video.name,
				type,
				overwrite: false,
				width: video.width,
				height: video.height,
				videoSource: video.videoSource,
			};
			if(hasAdvancedValues){
				item.customEncodes = [
					{
						...advancedValues,
						width: video.width,
						height: video.height,
					}
				];
			}
			return item;
		});

		const encodeRequest = {
			advvast: false,
			overwrite: false,
			type,
			items,
		};

		dispatch(transcodeVideo(libraryId, encodeRequest))
			.then((response) => {
				if(response?.success){
					onClose();
				}
			});
	},
})(BulkTranscodeModal);

BulkTranscodeModal.displayName = "BulkTranscodeModal";
BulkTranscodeModal.defaultProps = {
	onClose: noop,
};
